/**
 * Authentication Context
 * Manages user authentication state throughout the app
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  useRef,
  type ReactNode,
} from "react";
import type {
  User,
  UserWithToken,
  LoginRequest,
  RegisterRequest,
  ProfileUpdateRequest,
} from "@/types";
import {
  getStoredToken,
  setStoredToken,
  getStoredRefreshToken,
  setStoredRefreshToken,
  clearAuthStorage,
} from "@utils/storage";
import * as authService from "@services/auth";
import { STUDIO_URL, isStudioSso, studioLoginUrl } from "@/config/studio";

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  updateProfile: (data: ProfileUpdateRequest) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

// Token refresh interval (refresh 5 minutes before expiry)
const TOKEN_REFRESH_BUFFER = 5 * 60 * 1000; // 5 minutes in ms

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Schedule token refresh before expiry
  const scheduleTokenRefresh = useCallback((expiresIn: number) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    // Calculate when to refresh (5 minutes before expiry)
    const refreshIn = Math.max(0, expiresIn * 1000 - TOKEN_REFRESH_BUFFER);

    refreshTimerRef.current = setTimeout(async () => {
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        try {
          const response = await authService.refreshToken(refreshToken);
          setStoredToken(response.access_token);
          setStoredRefreshToken(response.refresh_token);
          // Schedule next refresh
          scheduleTokenRefresh(response.expires_in);
        } catch {
          // Refresh failed, user needs to re-authenticate
          setUser(null);
          clearAuthStorage();
        }
      }
    }, refreshIn);
  }, []);

  // Initialize auth state.
  useEffect(() => {
    const initAuth = async () => {
      if (isStudioSso()) {
        // studio_sso: there is no client-side token. Ask the backend who we
        // are - it reads the HttpOnly gml_sso cookie. 200 = authed, 401 = not.
        // Never navigate here: public routes must render for logged-out users.
        try {
          setUser(await authService.getCurrentUser());
        } catch {
          setUser(null);
        }
        setIsLoading(false);
        return;
      }

      // hub_jwt: initialize from the stored bearer token (legacy path).
      const token = getStoredToken();
      if (token) {
        try {
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          // Schedule refresh - assume 1 hour expiry if we don't know exact time
          scheduleTokenRefresh(60 * 60);
        } catch {
          // Token is invalid, try to refresh
          const refreshToken = getStoredRefreshToken();
          if (refreshToken) {
            try {
              const response = await authService.refreshToken(refreshToken);
              setStoredToken(response.access_token);
              setStoredRefreshToken(response.refresh_token);
              const currentUser = await authService.getCurrentUser();
              setUser(currentUser);
              scheduleTokenRefresh(response.expires_in);
            } catch {
              // Refresh also failed, clear everything
              clearAuthStorage();
            }
          } else {
            clearAuthStorage();
          }
        }
      }
      setIsLoading(false);
    };

    initAuth();

    // Cleanup timer on unmount
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [scheduleTokenRefresh]);

  // Listen for unauthorized events (401 responses)
  useEffect(() => {
    const handleUnauthorized = async () => {
      if (isStudioSso()) {
        // No refresh path - the Studio session is gone. Drop the user; routing
        // an unauthenticated user to Studio is ProtectedRoute -> LoginPage's job
        // (keeps the redirect in one place and avoids tight redirect loops).
        setUser(null);
        return;
      }

      // hub_jwt: try to refresh the token before giving up.
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        try {
          const response = await authService.refreshToken(refreshToken);
          setStoredToken(response.access_token);
          setStoredRefreshToken(response.refresh_token);
          scheduleTokenRefresh(response.expires_in);
          // Retry the original request - user should retry their action
          return;
        } catch {
          // Refresh failed
        }
      }
      setUser(null);
      clearAuthStorage();
    };

    window.addEventListener("auth:unauthorized", handleUnauthorized);
    return () => {
      window.removeEventListener("auth:unauthorized", handleUnauthorized);
    };
  }, [scheduleTokenRefresh]);

  const login = useCallback(
    async (credentials: LoginRequest) => {
      setIsLoading(true);
      try {
        const response: UserWithToken = await authService.login(credentials);
        setStoredToken(response.access_token);
        setStoredRefreshToken(response.refresh_token);
        setUser(response);
        scheduleTokenRefresh(response.expires_in);
      } finally {
        setIsLoading(false);
      }
    },
    [scheduleTokenRefresh],
  );

  const register = useCallback(
    async (data: RegisterRequest) => {
      setIsLoading(true);
      try {
        const response: UserWithToken = await authService.register(data);
        setStoredToken(response.access_token);
        setStoredRefreshToken(response.refresh_token);
        setUser(response);
        scheduleTokenRefresh(response.expires_in);
      } finally {
        setIsLoading(false);
      }
    },
    [scheduleTokenRefresh],
  );

  const logout = useCallback(async () => {
    // Clear refresh timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    if (isStudioSso()) {
      // Revoke the cross-subdomain Studio session, then best-effort clear the
      // Hub server session, then bounce to Studio login (which clears the
      // .graymatterlab.ai cookie globally on its side).
      try {
        await fetch(`${STUDIO_URL}/api/v1/auth/sso/revoke`, {
          method: "POST",
          credentials: "include",
        });
      } catch {
        // Ignore - we're logging out anyway
      }
      try {
        await authService.logout();
      } catch {
        // Ignore
      }
      setUser(null);
      window.location.assign(studioLoginUrl());
      return;
    }

    // hub_jwt: revoke refresh tokens server-side, clear local token storage.
    try {
      await authService.logout();
    } catch {
      // Ignore errors - we're logging out anyway
    }

    setUser(null);
    clearAuthStorage();
  }, []);

  const refreshUser = useCallback(async () => {
    if (isStudioSso()) {
      // Cookie-based: just re-fetch the current user.
      try {
        setUser(await authService.getCurrentUser());
      } catch {
        setUser(null);
      }
      return;
    }

    const token = getStoredToken();
    if (!token) {
      setUser(null);
      return;
    }

    try {
      const currentUser = await authService.getCurrentUser();
      setUser(currentUser);
    } catch {
      // Try refresh token
      const refreshToken = getStoredRefreshToken();
      if (refreshToken) {
        try {
          const response = await authService.refreshToken(refreshToken);
          setStoredToken(response.access_token);
          setStoredRefreshToken(response.refresh_token);
          const currentUser = await authService.getCurrentUser();
          setUser(currentUser);
          scheduleTokenRefresh(response.expires_in);
        } catch {
          setUser(null);
          clearAuthStorage();
        }
      } else {
        setUser(null);
        clearAuthStorage();
      }
    }
  }, [scheduleTokenRefresh]);

  const updateProfile = useCallback(async (data: ProfileUpdateRequest) => {
    const updatedUser = await authService.updateProfile(data);
    setUser(updatedUser);
  }, []);

  const value: AuthContextType = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    register,
    logout,
    refreshUser,
    updateProfile,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
